import {
  type CSSProperties,
  type FocusEvent,
  type MouseEvent,
  type RefObject,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

/**
 * Moving from the target to its bubble (and back) takes a moment: the bubble must not blink out.
 * One short delay and no more (#30): the only gap to cross is the few pixels between the two.
 * Going to ANOTHER target closes this bubble at once (see `ouverteParHover`).
 */
const DELAI_FERMETURE_MS = 100;
const MARGE_PX = 8;
const ECART_PX = 6;

/**
 * The closer of the bubble the pointer opened last. When the pointer enters another target, the
 * previous bubble is closed on the spot instead of waiting for its delay: two bubbles are never
 * on screen together (the passages of the text, #30, and the icon buttons, #37, share it). A
 * bubble opened by the focus is not closed this way: it stays while the focus is on its target.
 */
let ouverteParHover: (() => void) | null = null;

interface Position {
  top: number;
  left: number;
}

/** The line of the target the pointer is on (a passage that wraps has one box per line). */
function rectCible(element: HTMLElement, y: number | null): DOMRect {
  const rects = [...element.getClientRects()];
  const premier = rects[0] ?? element.getBoundingClientRect();
  if (y === null) return premier;
  return rects.find((rect) => y >= rect.top && y <= rect.bottom) ?? premier;
}

interface OptionsBulle {
  /** Centre the bubble on its target (an icon button) instead of starting at its left edge. */
  centree?: boolean;
}

/** What goes on the element the bubble explains. */
interface PropsCible<T extends HTMLElement> {
  onMouseEnter: (event: MouseEvent<T>) => void;
  onMouseMove: (event: MouseEvent<T>) => void;
  onMouseLeave: () => void;
  onFocus: (event: FocusEvent<T>) => void;
  onBlur: (event: FocusEvent<T>) => void;
}

/** What goes on the bubble itself (rendered in a portal by the caller, with its own look). */
interface PropsBulle {
  ref: RefObject<HTMLDivElement | null>;
  id: string;
  hidden: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  style: CSSProperties;
}

/**
 * The one mechanism of the bubbles of the app (WCAG 1.4.13): it opens on hover and on focus,
 * closes with Escape (wherever the focus is, leaving it where it is) and when the pointer and the
 * focus have left both the target and the bubble, and stays while the pointer is on it. The
 * bubble is `fixed`, so the caller renders it in a portal: the edge of a frame never cuts it. It
 * is kept in the window, under the target or above it when there is no room below. It should
 * always be in the page (hidden when closed) so that an `aria-describedby` has its target.
 *
 * Used by the passages of "Avant / après" (`MarqueBulle`) and by the icon buttons (`Infobulle`).
 */
export function useBulle<T extends HTMLElement>({ centree = false }: OptionsBulle = {}) {
  const id = useId();
  const cible = useRef<T>(null);
  const bulle = useRef<HTMLDivElement>(null);
  const minuterie = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const dernierY = useRef<number | null>(null);
  const [survol, setSurvol] = useState(false);
  const [focus, setFocus] = useState(false);
  const [position, setPosition] = useState<Position>({ top: 0, left: 0 });
  const ouverte = survol || focus;

  const placer = useCallback(() => {
    const element = cible.current;
    const contenu = bulle.current;
    if (element === null || contenu === null) return;
    const rect = rectCible(element, dernierY.current);
    const { width, height } = contenu.getBoundingClientRect();
    const debut = centree ? rect.left + rect.width / 2 - width / 2 : rect.left;
    const left = Math.max(MARGE_PX, Math.min(debut, window.innerWidth - width - MARGE_PX));
    const dessous = rect.bottom + ECART_PX;
    // Under the target, or above it when the window has no room below.
    const top =
      dessous + height + MARGE_PX <= window.innerHeight
        ? dessous
        : Math.max(MARGE_PX, rect.top - ECART_PX - height);
    setPosition({ top, left });
  }, [centree]);

  useLayoutEffect(() => {
    if (ouverte) placer();
  }, [ouverte, placer]);

  useEffect(() => {
    if (!ouverte) return;
    function fermer(event: globalThis.KeyboardEvent) {
      if (event.key !== 'Escape') return;
      setSurvol(false);
      setFocus(false);
    }
    document.addEventListener('keydown', fermer);
    window.addEventListener('resize', placer);
    window.addEventListener('scroll', placer, true);
    return () => {
      document.removeEventListener('keydown', fermer);
      window.removeEventListener('resize', placer);
      window.removeEventListener('scroll', placer, true);
    };
  }, [ouverte, placer]);

  /** Closes the hover of this bubble at once (another target was entered). */
  const fermerSurvol = useCallback(() => {
    clearTimeout(minuterie.current);
    setSurvol(false);
  }, []);

  useEffect(
    () => () => {
      clearTimeout(minuterie.current);
      if (ouverteParHover === fermerSurvol) ouverteParHover = null;
    },
    [fermerSurvol],
  );

  function entrer(y: number | null) {
    clearTimeout(minuterie.current);
    dernierY.current = y;
    if (ouverteParHover !== null && ouverteParHover !== fermerSurvol) ouverteParHover();
    ouverteParHover = fermerSurvol;
    setSurvol(true);
  }

  function sortir() {
    clearTimeout(minuterie.current);
    minuterie.current = setTimeout(() => setSurvol(false), DELAI_FERMETURE_MS);
  }

  const propsCible: PropsCible<T> = {
    onMouseEnter: (event) => entrer(event.clientY),
    onMouseMove: (event) => {
      dernierY.current = event.clientY;
    },
    onMouseLeave: sortir,
    onFocus: () => setFocus(true),
    onBlur: () => setFocus(false),
  };

  const propsBulle: PropsBulle = {
    ref: bulle,
    id,
    hidden: !ouverte,
    onMouseEnter: () => entrer(dernierY.current),
    onMouseLeave: sortir,
    style: { top: position.top, left: position.left },
  };

  return { cible, id, ouverte, propsCible, propsBulle };
}
